import React, { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Fab,
  Paper,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Tooltip,
  Slide,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EvaMaria from '../../assets/images/EvaMariaAIPicture.png';
import { useLang } from '../../utils/i18n';
import { SCHEDULE_CALL_EVENT } from '../../utils/scheduleCall';

// Falls back to local dev (python src\main.py in the Agent repo) when unset —
// set REACT_APP_AGENT_URL to the deployed Cloud Run URL for production builds.
const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'http://localhost:8080';

const DETECTED_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Auto-detected goes first (and is the default) — the rest is a small
// curated spread of common zones, not the full ~400-zone IANA list.
const TIMEZONE_OPTIONS = [
  { value: DETECTED_TIMEZONE, label: `Auto-detected (${DETECTED_TIMEZONE})` },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Mexico_City', label: 'Mexico City' },
  { value: 'America/Sao_Paulo', label: 'São Paulo' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Madrid', label: 'Madrid / Central Europe' },
  { value: 'Asia/Kolkata', label: 'India (Kolkata)' },
  { value: 'Asia/Shanghai', label: 'China (Shanghai)' },
  { value: 'Asia/Tokyo', label: 'Japan (Tokyo)' },
  { value: 'Australia/Sydney', label: 'Sydney' },
].filter((option, index, all) => all.findIndex((o) => o.value === option.value) === index);

const WELCOME = {
  en: "Hi! I'm Eva, Martín's AI assistant. Ask me anything about his background, projects, or skills.",
  es: '¡Hola! Soy Eva María, la asistente IA de Martín. Pregúntame sobre su experiencia, proyectos o habilidades.',
};

const PLACEHOLDER = {
  en: 'Ask me anything…',
  es: 'Escribe tu pregunta…',
};

const HEADER_LABEL = {
  en: 'Eva · AI Assistant',
  es: 'Eva · Asistente IA',
};

const FAB_TOOLTIP = {
  en: 'Ask Eva',
  es: 'Habla con Eva',
};

const ERROR_MSG = {
  en: 'Connection error. Please try again.',
  es: 'Error al conectar. Intenta de nuevo.',
};

const PENDING_BOOKING_PLACEHOLDER = {
  en: 'Please confirm or cancel the meeting above first…',
  es: 'Primero confirma o cancela la reunión de arriba…',
};

const SCHEDULE_TOOLTIP = {
  en: 'Schedule a call',
  es: 'Agendar una llamada',
};

const SCHEDULE_FORM_INTRO = {
  en: "Let's find a time on Martín's calendar! Just your name, email, and timezone to check availability and send the invite — takes a second.",
  es: '¡Busquemos un horario en el calendario de Martín! Solo tu nombre, correo y zona horaria para revisar disponibilidad y enviarte la invitación — toma un segundo.',
};

const SCHEDULE_NAME_LABEL = {
  en: 'Full name',
  es: 'Nombre completo',
};

const SCHEDULE_EMAIL_LABEL = {
  en: 'Email',
  es: 'Correo electrónico',
};

const SCHEDULE_TIMEZONE_LABEL = {
  en: 'Your timezone',
  es: 'Tu zona horaria',
};

const SCHEDULE_START_BUTTON = {
  en: 'Start',
  es: 'Comenzar',
};

const SCHEDULE_CANCEL_BUTTON = {
  en: 'Cancel',
  es: 'Cancelar',
};

const SCHEDULE_FORM_ERROR = {
  en: 'Please enter your name and a valid email.',
  es: 'Escribe tu nombre y un correo electrónico válido.',
};

const SCHEDULE_OPENING_MESSAGE = {
  en: "I'd like to schedule a call.",
  es: 'Me gustaría agendar una llamada.',
};

const CONFIRM_BOOKING_BUTTON = {
  en: 'Confirm booking',
  es: 'Confirmar llamada',
};

const BOOKING_CONFIRMED_NOTICE = {
  en: 'Almost there — check your inbox and click the link to lock in your call. Start a new chat if you\'d like to keep talking.',
  es: 'Casi listo — revisa tu correo y haz clic en el enlace para reservar tu llamada. Inicia un nuevo chat si quieres seguir hablando.',
};

const NEW_CHAT_BUTTON = {
  en: 'Start new chat',
  es: 'Iniciar nuevo chat',
};

// How fast the simulated "typing" reveal of the final answer runs. The text
// already arrives all at once (compose_response doesn't generate live) — this
// just reveals it progressively client-side so it still feels like the old
// token-streamed RAG answers, without needing real streaming plumbing through
// the agent for something visitors can't actually tell apart.
const TYPE_CHARS_PER_TICK = 3;
const TYPE_TICK_MS = 15;

function generateId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function TypingDots() {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', py: 0.25 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            animationDelay: `${i * 0.2}s`,
            '@keyframes chatPulse': {
              '0%, 100%': { transform: 'scale(0.6)', opacity: 0.35 },
              '50%': { transform: 'scale(1)', opacity: 1 },
            },
            animation: 'chatPulse 1.1s ease-in-out infinite',
          }}
        />
      ))}
    </Box>
  );
}

function AssistantAvatar() {
  return (
    <Avatar
      src={EvaMaria}
      alt="Eva"
      sx={{
        width: 38,
        height: 38,
        flexShrink: 0,
        alignSelf: 'flex-end',
        border: '1.5px solid rgba(6,182,212,0.35)',
      }}
    />
  );
}

function HeaderAvatar() {
  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <Avatar
        src={EvaMaria}
        alt="Eva"
        sx={{
          width: 52,
          height: 52,
          border: '1.5px solid rgba(6,182,212,0.35)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 1,
          right: 1,
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: '#22c55e',
          boxShadow: '0 0 6px #22c55e88',
          border: '1.5px solid #0c1828',
        }}
      />
    </Box>
  );
}

function Bubble({ role, text }) {
  const isUser = role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 1, alignItems: 'flex-end' }}>
      {!isUser && <AssistantAvatar />}
      <Box
        sx={{
          maxWidth: '78%',
          px: 2,
          py: 1,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          backgroundColor: isUser ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.05)',
          border: isUser
            ? '1px solid rgba(6,182,212,0.28)'
            : '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <Typography variant="body2" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ChatBox() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [awaitingFirstChunk, setAwaitingFirstChunk] = useState(false);
  const [statusLabel, setStatusLabel] = useState('');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [visitorTimezone, setVisitorTimezone] = useState(DETECTED_TIMEZONE);
  const [scheduleFormError, setScheduleFormError] = useState(false);
  const [agentConversationId, setAgentConversationId] = useState(null);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [lang] = useLang();
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const inactivityTimer = useRef(null);

  const clearInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
  };

  const resetInactivityTimer = () => {
    clearInactivityTimer();
    inactivityTimer.current = setTimeout(() => setOpen(false), 3 * 60 * 1000);
  };

  const resetChat = () => {
    setMessages([{ role: 'assistant', text: WELCOME[lang] ?? WELCOME.en }]);
    setInput('');
    setShowScheduleForm(false);
    setPendingBooking(null);
    setBookingConfirmed(false);
    setAgentConversationId(generateId());
    setVisitorName('');
    setVisitorEmail('');
    setVisitorTimezone(DETECTED_TIMEZONE);
    resetInactivityTimer();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Set welcome message, ensure a conversation id exists, and start the
  // inactivity timer when opened; clear the timer when closed. Every message
  // — plain questions and scheduling alike — goes through the same agent
  // conversation from the very first message now.
  useEffect(() => {
    if (open) {
      if (messages.length === 0) {
        setMessages([{ role: 'assistant', text: WELCOME[lang] ?? WELCOME.en }]);
      }
      setAgentConversationId((current) => current ?? generateId());
      resetInactivityTimer();
      setTimeout(() => inputRef.current?.focus(), 150);
    } else {
      clearInactivityTimer();
    }
    return clearInactivityTimer;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lets "Book a call" buttons elsewhere on the site (Footer, About, ...)
  // open this widget straight into the scheduling form.
  useEffect(() => {
    const handleOpenSchedule = () => {
      setOpen(true);
      setShowScheduleForm(true);
      setScheduleFormError(false);
    };
    window.addEventListener(SCHEDULE_CALL_EVENT, handleOpenSchedule);
    return () => window.removeEventListener(SCHEDULE_CALL_EVENT, handleOpenSchedule);
  }, []);

  // Scroll to bottom on new content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, awaitingFirstChunk]);

  const revealTyped = (fullText, next) =>
    new Promise((resolve) => {
      let shown = 0;
      const id = setInterval(() => {
        shown = Math.min(shown + TYPE_CHARS_PER_TICK, fullText.length);
        setMessages([...next, { role: 'assistant', text: fullText.slice(0, shown) }]);
        if (shown >= fullText.length) {
          clearInterval(id);
          resolve();
        }
      }, TYPE_TICK_MS);
    });

  // Agent's /invoke/stream sends progress lines ("Thinking...\n", ...), then
  // a blank line, then the full final state as one JSON blob. Every message
  // — plain Q&A included — goes through this now; the agent's own planner
  // decides whether that means retrieve_knowledge or schedule_step.
  const sendToAgent = async (q, next, conversationId, name, email, timezone) => {
    try {
      const res = await fetch(`${AGENT_URL}/invoke/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: q,
          visitor_name: name || undefined,
          visitor_email: email || undefined,
          visitor_timezone: timezone,
          language: lang,
        }),
      });
      if (!res.ok || !res.body) throw new Error(res.status);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let raw = '';
      let splitAt = -1;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
        if (splitAt === -1) {
          splitAt = raw.indexOf('\n\n');
          if (splitAt === -1) {
            const lines = raw.split('\n').filter(Boolean);
            setStatusLabel(lines[lines.length - 1] ?? '');
          }
        }
      }

      if (splitAt === -1) throw new Error('no result from agent');
      const finalState = JSON.parse(raw.slice(splitAt + 2));
      if (finalState.error) throw new Error(finalState.error);

      const assistantMessages = (finalState.messages || []).filter((m) => m.role === 'assistant');
      const answer = assistantMessages[assistantMessages.length - 1]?.content
        ?? (ERROR_MSG[lang] ?? ERROR_MSG.en);

      setAwaitingFirstChunk(false);
      setStatusLabel('');
      await revealTyped(answer, next);
      setPendingBooking(finalState.action_type === 'meeting' ? finalState.pending_action : null);
      if (finalState.action_type === 'needs_contact_info') {
        // Don't just tell the visitor to go find the button — show it.
        setShowScheduleForm(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Agent request failed:', err);
      setAwaitingFirstChunk(false);
      setMessages([...next, { role: 'assistant', text: ERROR_MSG[lang] ?? ERROR_MSG.en }]);
    } finally {
      setLoading(false);
      setAwaitingFirstChunk(false);
      setStatusLabel('');
    }
  };

  const startScheduling = () => {
    setShowScheduleForm(true);
    setScheduleFormError(false);
  };

  const cancelScheduleForm = () => {
    setShowScheduleForm(false);
    setScheduleFormError(false);
  };

  const submitScheduleForm = () => {
    const name = visitorName.trim();
    const email = visitorEmail.trim();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!name || !emailValid) {
      setScheduleFormError(true);
      return;
    }
    setScheduleFormError(false);
    setVisitorName(name);
    setVisitorEmail(email);
    setShowScheduleForm(false);
    resetInactivityTimer();

    // Always send something that actively continues the conversation —
    // a silent "just update the fields" here previously left the widget
    // looking frozen, since nothing visibly happened until the next message.
    // If a specific meeting was already staged for confirmation, re-propose
    // it (same time, corrected details) — the earlier, now-abandoned
    // proposal is simply left unconfirmed on the calendar. Otherwise, resend
    // the opening scheduling request so the agent picks the flow back up
    // (whether this is the very first time, or a correction mid-negotiation).
    const message = pendingBooking
      ? `Please keep the same proposed time (${pendingBooking.start} to ${pendingBooking.end}), just update my contact details.`
      : SCHEDULE_OPENING_MESSAGE[lang] ?? SCHEDULE_OPENING_MESSAGE.en;

    const next = [...messages, { role: 'user', text: message }];
    setMessages(next);
    setLoading(true);
    setAwaitingFirstChunk(true);
    sendToAgent(message, next, agentConversationId, name, email, visitorTimezone);
  };

  const confirmBooking = async (approved) => {
    setConfirming(true);
    try {
      const res = await fetch(`${AGENT_URL}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: agentConversationId, approved }),
      });
      const data = await res.json();
      const text = data.message ?? data.error ?? (ERROR_MSG[lang] ?? ERROR_MSG.en);
      setMessages((current) => [...current, { role: 'assistant', text }]);
      // The booking itself isn't finalized here anymore — approving in chat
      // now just sends a one-click confirmation link to the visitor's email
      // (see /confirm in the agent). data.pending_email_confirmation is only
      // true once that email actually went out; a rejection, an error, or a
      // daily-limit message all come back without it, and should leave the
      // chat open rather than locking it.
      if (approved && data.pending_email_confirmation) {
        setBookingConfirmed(true);
      }
    } catch {
      setMessages((current) => [
        ...current,
        { role: 'assistant', text: ERROR_MSG[lang] ?? ERROR_MSG.en },
      ]);
    } finally {
      setPendingBooking(null);
      setConfirming(false);
    }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    resetInactivityTimer();

    const next = [...messages, { role: 'user', text: q }];
    setMessages(next);
    setLoading(true);
    setAwaitingFirstChunk(true);
    await sendToAgent(q, next, agentConversationId, visitorName, visitorEmail, visitorTimezone);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Chat panel */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 104,
            right: 24,
            width: { xs: 'calc(100vw - 32px)', sm: 460 },
            maxWidth: 'calc(100vw - 32px)',
            height: 480,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(6,182,212,0.22)',
            borderRadius: '16px',
            overflow: 'hidden',
            zIndex: 1300,
            boxShadow: '0 8px 40px rgba(0,0,0,0.65)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              pl: 2,
              pr: 2,
              py: 1.25,
              background:
                'linear-gradient(90deg, rgba(6,182,212,0.12) 0%, rgba(6,182,212,0.04) 100%)',
              borderBottom: '1px solid rgba(6,182,212,0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <HeaderAvatar />
            <Typography variant="h6" sx={{ flex: 1, color: 'primary.main', fontWeight: 700, fontSize: '1.05rem' }}>
              {HEADER_LABEL[lang] ?? HEADER_LABEL.en}
            </Typography>
            {!showScheduleForm && (
              <Tooltip title={SCHEDULE_TOOLTIP[lang] ?? SCHEDULE_TOOLTIP.en}>
                <IconButton
                  size="small"
                  onClick={startScheduling}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                  aria-label="Schedule a call"
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={lang === 'es' ? 'Nueva conversación' : 'New conversation'}>
              <IconButton
                size="small"
                onClick={resetChat}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                aria-label="New conversation"
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              aria-label="Close chat"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pl: 2.5,
              pr: 2,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} />
            ))}
            {awaitingFirstChunk && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'flex-end' }}>
                <AssistantAvatar />
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: '16px 16px 16px 4px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TypingDots />
                  {statusLabel && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {statusLabel}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {pendingBooking && !awaitingFirstChunk && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: '46px', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => confirmBooking(true)}
                  disabled={confirming}
                  sx={{ textTransform: 'none' }}
                >
                  {CONFIRM_BOOKING_BUTTON[lang] ?? CONFIRM_BOOKING_BUTTON.en}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => confirmBooking(false)}
                  disabled={confirming}
                  sx={{ textTransform: 'none' }}
                >
                  {SCHEDULE_CANCEL_BUTTON[lang] ?? SCHEDULE_CANCEL_BUTTON.en}
                </Button>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Disclaimer */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem', px: 2, pb: 0.5 }}
          >
            {lang === 'es'
              ? 'Las conversaciones y datos de uso limitados pueden registrarse temporalmente para mejorar este asistente'
              : 'Conversations and limited usage data may be temporarily logged to improve this assistant'}
          </Typography>

          {/* Input, or the name/email/timezone form when scheduling a call,
              or a locked notice once a call is actually booked — a booked
              meeting is a closed conversation from the agent's point of
              view; continuing to chat here wouldn't do anything useful, so
              the visitor is pointed at starting a fresh chat instead. */}
          {bookingConfirmed ? (
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 22, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.85)', flex: 1 }}>
                {BOOKING_CONFIRMED_NOTICE[lang] ?? BOOKING_CONFIRMED_NOTICE.en}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={resetChat}
                startIcon={<RestartAltIcon fontSize="small" />}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {NEW_CHAT_BUTTON[lang] ?? NEW_CHAT_BUTTON.en}
              </Button>
            </Box>
          ) : showScheduleForm ? (
            <Box
              sx={{
                pl: 2.5,
                pr: 2,
                py: 1.5,
                borderTop: '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: 'rgba(6,182,212,0.06)',
                  border: '1px solid rgba(6,182,212,0.18)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  mb: 0.5,
                }}
              >
                <CalendarMonthIcon sx={{ color: 'rgba(125,211,252,0.8)', fontSize: 20, mt: '1px' }} />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(125,211,252,0.85)', fontWeight: 600, lineHeight: 1.4 }}
                >
                  {SCHEDULE_FORM_INTRO[lang] ?? SCHEDULE_FORM_INTRO.en}
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                label={SCHEDULE_NAME_LABEL[lang] ?? SCHEDULE_NAME_LABEL.en}
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                error={scheduleFormError && !visitorName.trim()}
              />
              <TextField
                fullWidth
                size="small"
                type="email"
                label={SCHEDULE_EMAIL_LABEL[lang] ?? SCHEDULE_EMAIL_LABEL.en}
                value={visitorEmail}
                onChange={(e) => setVisitorEmail(e.target.value)}
                error={scheduleFormError}
              />
              <TextField
                select
                fullWidth
                size="small"
                label={SCHEDULE_TIMEZONE_LABEL[lang] ?? SCHEDULE_TIMEZONE_LABEL.en}
                value={visitorTimezone}
                onChange={(e) => setVisitorTimezone(e.target.value)}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                ))}
              </TextField>
              {scheduleFormError && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                  {SCHEDULE_FORM_ERROR[lang] ?? SCHEDULE_FORM_ERROR.en}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={cancelScheduleForm} sx={{ textTransform: 'none' }}>
                  {SCHEDULE_CANCEL_BUTTON[lang] ?? SCHEDULE_CANCEL_BUTTON.en}
                </Button>
                <Button size="small" variant="contained" onClick={submitScheduleForm} sx={{ textTransform: 'none' }}>
                  {SCHEDULE_START_BUTTON[lang] ?? SCHEDULE_START_BUTTON.en}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                pl: 2.5,
                pr: 1.5,
                py: 1.5,
                borderTop: '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
                flexShrink: 0,
              }}
            >
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                multiline
                maxRows={3}
                placeholder={
                  pendingBooking
                    ? PENDING_BOOKING_PLACEHOLDER[lang] ?? PENDING_BOOKING_PLACEHOLDER.en
                    : PLACEHOLDER[lang] ?? PLACEHOLDER.en
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                disabled={loading || Boolean(pendingBooking)}
              />
              <IconButton
                onClick={send}
                disabled={!input.trim() || loading || Boolean(pendingBooking)}
                sx={{
                  color: 'primary.main',
                  mb: 0.125,
                  '&:disabled': { color: 'text.secondary' },
                }}
                aria-label="Send message"
              >
                <SendIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
      </Slide>

      {/* Floating action button */}
      <Tooltip title={FAB_TOOLTIP[lang] ?? FAB_TOOLTIP.en} placement="left">
        <Fab
          aria-label="Open AI assistant chat"
          onClick={() => setOpen((v) => !v)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            width: 68,
            height: 68,
            backgroundColor: 'rgba(6,182,212,0.12)',
            color: 'primary.main',
            border: '1.5px solid',
            borderColor: open ? 'primary.main' : 'rgba(6,182,212,0.4)',
            boxShadow: '0 4px 20px rgba(6,182,212,0.25)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(6,182,212,0.22)',
              borderColor: 'primary.main',
              boxShadow: '0 4px 24px rgba(6,182,212,0.45)',
            },
          }}
        >
          {open ? <CloseIcon sx={{ fontSize: 29 }} /> : <ChatIcon sx={{ fontSize: 29 }} />}
        </Fab>
      </Tooltip>
    </>
  );
}
